import { Fragment, memo, useState, type CSSProperties, type ReactElement, type ReactNode } from 'react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  SEPARATOR_ID,
  reconcileMenuLayout,
  effectiveCollapsed,
  moveMenuItem,
  type MenuLayout,
} from '@lapo/core';
import { MaterialIcon } from './MaterialIcon.js';

/**
 * Left sidebar (Logseq-style): primary nav, Favorites, Recents, Create. Presentational — data is
 * passed in so it stays testable without a store. On narrow viewports it becomes an off-canvas drawer
 * (driven by the shell's `data-sidebar` attribute), dismissed by tapping the scrim or the toolbar's
 * sidebar toggle.
 *
 * FOLD (docs/plans/sidebar-menu-customization-fold.md): the PRIMARY NAV is user-customizable.
 * `layout` (a {@link MenuLayout}) drives the item order and the single collapsible separator; a
 * long-press lifts a row into a drag (dnd-kit press-delay sensor = long-press on desktop AND touch),
 * while a quick tap still navigates. Everything remains presentational — the app owns persistence and
 * passes `onReorder`/`onToggleCollapse` callbacks. When `layout` is omitted the nav renders in the
 * given order with no separator (backward-compatible).
 */
export interface SidebarItem {
  id: string;
  label: string;
  /** The row's leading icon — an emoji string or a rendered node (e.g. a Material Symbol). */
  emoji?: ReactNode;
  /** Whether this note is currently starred — drives the trailing star toggle's filled state. */
  starred?: boolean;
}
export interface SidebarNavItem {
  id: string;
  label: string;
  icon: ReactElement;
  active?: boolean;
  /** Optional control rendered to the right of the row (e.g. the journal date navigator). */
  accessory?: ReactElement;
  /** Nested sub-items, revealed indented beneath this row while the parent is `active`. */
  children?: SidebarNavItem[];
}
export interface SidebarProps {
  nav: SidebarNavItem[];
  favorites: SidebarItem[];
  recents: SidebarItem[];
  onSelect?: (id: string) => void;
  onNav?: (id: string) => void;
  onCreate?: () => void;
  /** Star/unstar a favorites or recents row. When omitted, no star toggle is rendered. */
  onToggleStar?: (id: string) => void;
  /** Dismiss a Recent row (hide it until the doc is opened again). When set, Recent rows show an X
   * (in place of the star) that calls this. Favorites keep their star. */
  onDismiss?: (id: string) => void;
  /** Shown under Favorites when there are none, so the section never reads as broken. */
  emptyFavorites?: string;
  /** Shown under Recent when there are none. */
  emptyRecents?: string;
  /**
   * When set (a `.dmg` URL), render a small "download the Mac app" icon beside Create. The caller
   * passes this ONLY for macOS web visitors (not inside the desktop app, not on other platforms), so
   * the icon simply doesn't appear anywhere else.
   */
  macDownloadUrl?: string;
  /** FOLD — the user's customized nav layout (order + separator position + collapsed). */
  layout?: MenuLayout;
  /** FOLD — called with the full new id order (incl. the separator sentinel) after a drag-reorder. */
  onReorder?: (order: string[]) => void;
  /** FOLD — called when the separator is clicked to collapse/expand the below zone. */
  onToggleCollapse?: () => void;
}

function ItemRow({
  item,
  onSelect,
  onToggleStar,
  onDismiss,
}: {
  item: SidebarItem;
  onSelect?: (id: string) => void;
  onToggleStar?: (id: string) => void;
  onDismiss?: (id: string) => void;
}): ReactElement {
  return (
    <li className="lapo-side-itemrow">
      <button type="button" className="lapo-side-item" onClick={() => onSelect?.(item.id)}>
        <span className="lapo-side-emoji">{item.emoji ?? '📄'}</span>
        <span className="lapo-side-label">{item.label}</span>
      </button>
      {onDismiss ? (
        <button
          type="button"
          className="lapo-side-dismiss"
          title="Remove from recent"
          aria-label="Remove from recent"
          onClick={() => onDismiss(item.id)}
        >
          <MaterialIcon name="close" size={16} />
        </button>
      ) : onToggleStar ? (
        <button
          type="button"
          className={`lapo-side-star${item.starred ? ' lapo-starred' : ''}`}
          title={item.starred ? 'Remove from favorites' : 'Add to favorites'}
          aria-label={item.starred ? 'Remove from favorites' : 'Add to favorites'}
          aria-pressed={item.starred ?? false}
          onClick={() => onToggleStar(item.id)}
        >
          <MaterialIcon name="star" size={16} />
        </button>
      ) : null}
    </li>
  );
}

/**
 * One sortable primary-nav row (parent item). Long-press lifts it (the DndContext's press-delay
 * sensor gates activation); a quick tap navigates. The accessory and the children-chevron stop
 * pointer-down propagation so pressing THEM never starts a row drag.
 */
function SortableNavRow({
  item,
  open,
  onNav,
  onToggleChildren,
}: {
  item: SidebarNavItem;
  open: boolean;
  onNav?: (id: string) => void;
  onToggleChildren?: () => void;
}): ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const hasChildren = !!item.children?.length;
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Keep the lifted row above its siblings and hint at the grab.
    zIndex: isDragging ? 5 : undefined,
    cursor: isDragging ? 'grabbing' : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`lapo-side-navrow${item.active ? ' lapo-active' : ''}${isDragging ? ' lapo-side-dragging' : ''}`}
      {...attributes}
      {...listeners}
    >
      <button
        type="button"
        className={`lapo-side-navitem${item.active ? ' lapo-active' : ''}`}
        onClick={() => onNav?.(item.id)}
      >
        <span className="lapo-side-navicon">{item.icon}</span>
        <span className="lapo-side-label">{item.label}</span>
      </button>
      {item.accessory ? (
        // Stop the drag sensor from claiming presses on the accessory (e.g. the journal date nav).
        <span className="lapo-side-navaccessory" onPointerDown={(e) => e.stopPropagation()}>
          {item.accessory}
        </span>
      ) : null}
      {hasChildren ? (
        <button
          type="button"
          className="lapo-side-navchevron"
          aria-label={open ? `Collapse ${item.label}` : `Expand ${item.label}`}
          aria-expanded={open}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onToggleChildren}
        >
          <MaterialIcon name={open ? 'expand_less' : 'expand_more'} size={18} />
        </button>
      ) : null}
    </div>
  );
}

/**
 * The single FOLD separator row — a thin divider rule + a chevron arrow (spec D10: line + arrow, no
 * label). The whole row is the collapse/expand toggle; a long-press lifts it to reposition (it is a
 * sortable item too). `collapsed` reflects the EFFECTIVE state (auto-expand may override the stored
 * value), so the arrow always matches what's shown.
 */
function SortableSeparator({
  collapsed,
  hiddenCount,
  onToggleCollapse,
}: {
  collapsed: boolean;
  hiddenCount: number;
  onToggleCollapse?: () => void;
}): ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: SEPARATOR_ID });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 5 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`lapo-side-separator${isDragging ? ' lapo-side-dragging' : ''}`}
      {...attributes}
      {...listeners}
    >
      <button
        type="button"
        className="lapo-side-separator-toggle"
        aria-expanded={!collapsed}
        aria-label={collapsed ? `Show ${hiddenCount} more menu item${hiddenCount === 1 ? '' : 's'}` : 'Hide the menu items below'}
        title={collapsed ? 'Show more' : 'Hide below'}
        onClick={onToggleCollapse}
      >
        <span className="lapo-side-separator-rule" aria-hidden="true" />
        <MaterialIcon name={collapsed ? 'expand_more' : 'expand_less'} size={16} />
      </button>
    </div>
  );
}

// Memoized (audit F3). Hoisted so the export can wrap it above the definition.
export const Sidebar = memo(SidebarBase);

function SidebarBase({
  nav,
  favorites,
  recents,
  onSelect,
  onNav,
  onCreate,
  onToggleStar,
  onDismiss,
  emptyFavorites,
  emptyRecents,
  macDownloadUrl,
  layout,
  onReorder,
  onToggleCollapse,
}: SidebarProps): ReactElement {
  // Per-item expand/collapse override for nav rows with children (keyed by id). Absent = follow active.
  const [openOverride, setOpenOverride] = useState<Record<string, boolean>>({});

  const byId = new Map(nav.map((n) => [n.id, n] as const));
  const availableIds = nav.map((n) => n.id);
  // Reconcile the stored layout against what's on offer (drops stale ids, appends new ones before the
  // separator — spec D7). When no layout is supplied, fall back to the natural order with no separator.
  const reconciled = layout
    ? reconcileMenuLayout(layout, availableIds)
    : { order: availableIds, separatorIndex: -1, visibleIds: availableIds, hiddenIds: [] as string[] };
  const activeId = nav.find((n) => n.active)?.id;
  // Effective collapse: auto-expand when the active view lives in the hidden zone (spec D9) without
  // rewriting the stored preference.
  const collapsed = layout ? effectiveCollapsed(layout, reconciled, activeId) : false;

  // The ids we actually render as sortable rows: everything when expanded, only the visible zone +
  // the separator when collapsed (you expand to reorder hidden rows). dnd-kit's `items` must match.
  const renderIds = collapsed ? [...reconciled.visibleIds, SEPARATOR_ID] : reconciled.order;

  const sensors = useSensors(
    // Press-and-hold to lift (≈220ms) with a small tolerance so a quick tap falls through to onClick
    // (navigate / toggle) — this is the "long-press to reorder on desktop and mobile" behavior (D3).
    useSensor(PointerSensor, { activationConstraint: { delay: 220, tolerance: 6 } }),
    // Keyboard sensor is wired for the FOLD-08 a11y fast-follow; harmless to include now.
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // FOLD-08 — a human label for an id so keyboard/screen-reader announcements read the item name
  // (not the raw id). The separator announces itself plainly.
  function labelFor(id: string | number): string {
    if (id === SEPARATOR_ID) return 'menu separator';
    return byId.get(String(id))?.label ?? String(id);
  }
  // Screen-reader announcements for the keyboard drag flow (space to lift, arrows to move, space to
  // drop, escape to cancel). dnd-kit renders these into a polite live region.
  const announcements = {
    onDragStart: ({ active }: { active: { id: string | number } }) => `Picked up ${labelFor(active.id)}.`,
    onDragOver: ({ active, over }: { active: { id: string | number }; over: { id: string | number } | null }) =>
      over ? `${labelFor(active.id)} is over ${labelFor(over.id)}.` : `${labelFor(active.id)} is no longer over a drop target.`,
    onDragEnd: ({ active, over }: { active: { id: string | number }; over: { id: string | number } | null }) =>
      over ? `${labelFor(active.id)} was dropped over ${labelFor(over.id)}.` : `${labelFor(active.id)} was dropped.`,
    onDragCancel: ({ active }: { active: { id: string | number } }) => `Reordering ${labelFor(active.id)} was cancelled.`,
  };

  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    // Move within the FULL reconciled order (which includes any collapsed/hidden ids) so hidden rows
    // are never dropped when reordering while collapsed.
    const next = moveMenuItem(reconciled.order, String(active.id), String(over.id));
    if (next !== reconciled.order) onReorder?.(next);
  }

  function renderNavRow(id: string): ReactElement | null {
    if (id === SEPARATOR_ID) {
      return (
        <SortableSeparator
          key={SEPARATOR_ID}
          collapsed={collapsed}
          hiddenCount={reconciled.hiddenIds.length}
          onToggleCollapse={onToggleCollapse}
        />
      );
    }
    const n = byId.get(id);
    if (!n) return null;
    const hasChildren = !!n.children?.length;
    // Expansion of a parent's children defaults to its active state (so deep-links reveal children),
    // but the chevron lets the user override it either way for this session.
    const open = hasChildren ? (openOverride[n.id] ?? !!n.active) : false;
    return (
      <Fragment key={n.id}>
        <SortableNavRow
          item={n}
          open={open}
          onNav={onNav}
          onToggleChildren={() => setOpenOverride((o) => ({ ...o, [n.id]: !open }))}
        />
        {hasChildren && open ? (
          <div className="lapo-side-subnav">
            {n.children?.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`lapo-side-navitem lapo-side-subitem${c.active ? ' lapo-active' : ''}`}
                onClick={() => onNav?.(c.id)}
              >
                <span className="lapo-side-navicon">{c.icon}</span>
                <span className="lapo-side-label">{c.label}</span>
              </button>
            ))}
          </div>
        ) : null}
      </Fragment>
    );
  }

  return (
    <aside className="lapo-sidebar" data-testid="sidebar" data-tauri-drag-region>
      <nav className="lapo-side-nav" data-collapsed={collapsed ? 'true' : 'false'}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
          accessibility={{ announcements }}
        >
          <SortableContext items={renderIds} strategy={verticalListSortingStrategy}>
            {renderIds.map((id) => renderNavRow(id))}
          </SortableContext>
        </DndContext>
      </nav>

      <div className="lapo-side-section">
        <div className="lapo-side-heading">★ Favorites</div>
        {favorites.length ? (
          <ul className="lapo-side-list">
            {favorites.map((f) => (
              <ItemRow key={f.id} item={{ ...f, starred: f.starred ?? true }} onSelect={onSelect} onToggleStar={onToggleStar} />
            ))}
          </ul>
        ) : (
          <p className="lapo-side-empty">{emptyFavorites ?? 'Star a page to pin it here.'}</p>
        )}
      </div>

      <div className="lapo-side-section">
        <div className="lapo-side-heading">◷ Recent</div>
        {recents.length ? (
          <ul className="lapo-side-list">
            {recents.map((r) => (
              <ItemRow key={r.id} item={r} onSelect={onSelect} onDismiss={onDismiss} />
            ))}
          </ul>
        ) : (
          <p className="lapo-side-empty">{emptyRecents ?? 'Recent documents appear here.'}</p>
        )}
      </div>

      <div className="lapo-side-create-row">
        <button type="button" className="lapo-side-create" onClick={onCreate}>
          + Create
        </button>
        {macDownloadUrl ? (
          <a
            className="lapo-side-download"
            href={macDownloadUrl}
            title="Download the Mac app"
            aria-label="Download the Mac app"
          >
            <MaterialIcon name="install_desktop" size={18} />
          </a>
        ) : null}
      </div>
    </aside>
  );
}
